import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Documents Franklin made rather than someone uploading them, by
 * `documents.source` — today the additions PDF it files in a project's Brief
 * folder after a confirmed version changed the brief's additions (pipeline
 * `brief_appendices`). The lists mark them with Franklin's logo.
 *
 * The logo is read per building: a handful per building at most, so one small
 * query serves every list and the Timeline (whose RPC rows carry no `source`)
 * from one cache entry. The documents realtime bridge refreshes it, since
 * every render files the PDF as a new row.
 *
 * Hiding a row (the Brief page) must not wait on that query, or a PDF filed
 * after it last ran shows until it catches up: the folder rows carry `source`
 * themselves, so `isFranklinMade` decides from the row alone.
 */
export const FRANKLIN_MADE_SOURCES: readonly string[] = ["brief_appendices"];

/** Whether a row Franklin filed itself, from the row's own `source`. */
export function isFranklinMade(doc: { source?: string | null }): boolean {
  return doc.source != null && FRANKLIN_MADE_SOURCES.includes(doc.source);
}

/**
 * PostgREST `or` filter keeping only what people uploaded — for feeds that
 * report activity (the dashboard's What's new), where a PDF Franklin filed
 * itself is not news. `source` is null for an upload, so it must be kept
 * explicitly: `not.in` alone drops nulls.
 */
export const NOT_FRANKLIN_MADE_FILTER = `source.is.null,source.not.in.(${FRANKLIN_MADE_SOURCES.join(",")})`;

export const franklinMadeKey = (buildingId: string) =>
  ["franklin-made-documents", buildingId] as const;

export async function fetchFranklinMadeDocumentIds(buildingId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("documents")
    .select("id")
    .eq("building_id", Number(buildingId))
    .in("source", [...FRANKLIN_MADE_SOURCES])
    .eq("is_deleted", false);
  if (error) throw error;
  return new Set(((data ?? []) as Array<{ id: number }>).map((row) => String(row.id)));
}

const NONE: ReadonlySet<string> = new Set();

/** Ids of the building's Franklin-made documents; empty while loading or on failure. */
export function useFranklinMadeDocumentIds(buildingId: string | undefined): ReadonlySet<string> {
  const query = useQuery({
    queryKey: franklinMadeKey(buildingId ?? ""),
    queryFn: () => fetchFranklinMadeDocumentIds(buildingId as string),
    enabled: !!buildingId && /^\d+$/.test(buildingId),
    // A new one arrives seconds after a brief version is confirmed.
    staleTime: 30_000,
    // A marker: a failed read shows no logo rather than an error.
    retry: false,
  });
  return query.data ?? NONE;
}

/**
 * The additions PDF is filed under its confirming person's language
 * ("Täiendused.pdf" / "Additions.pdf", "(Franklin)" added when the name was
 * taken), and shown under the viewer's: the stem is swapped, any suffix kept.
 * The names it carried before the section was renamed ("Lisad", "Appendix",
 * "Appendices") are shown under the new ones too. Other names pass through.
 */
const ADDITIONS_NAME =
  /^(?:Täiendused|Additions|Lisad|Appendix|Appendices)((?: \(Franklin(?: \d+)?\))?\.pdf)$/i;

export function localiseFranklinFilename(filename: string, language: string): string {
  const match = ADDITIONS_NAME.exec(filename);
  if (!match) return filename;
  return `${language === "en" ? "Additions" : "Täiendused"}${match[1]}`;
}

/** The name a row shows: the viewer-language name for Franklin's file, else the stored one. */
export function useFranklinDisplayName(
  buildingId: string | undefined,
  documentId: string | number,
  filename: string,
  language: string
): string {
  const ids = useFranklinMadeDocumentIds(buildingId);
  return ids.has(String(documentId)) ? localiseFranklinFilename(filename, language) : filename;
}
